/* ZSportsTech — netsessions-calendar.js
   The month calendar + slot column used by BOTH booking popups:
     • My Sessions  → book a paid net-session or clinic credit
     • Z-Bat Clinic → pick a slot before paying (slot-first)

   It renders into a container and calls back when a slot is chosen. It never
   talks to the backend itself — the caller supplies `loadSlots(state)`, so the
   clinic popup and My Sessions can differ in what they ask for without this
   file knowing about services, balances or holds.

   Loaded by snippets/netsessions-config.liquid, after netsessions-api.js.
   Requires window.SCNS. Exposes SCNS.calendar(opts) -> { destroy, refresh, state }.
   ─────────────────────────────────────────────────── */
(function () {
  'use strict';

  var S = window.SCNS;
  if (!S || S.calendar) return;

  var MAX_DAYS = 180;   // 6-month rolling window — must match CFG.MAX_DAYS on the backend

  /* An off-hours slot needs the owner to confirm it, so it must start far enough
     ahead for them to answer. Must match CFG.REQUEST_MIN_LEAD_MIN on the backend,
     which is the real authority (it returns `too_soon_to_request`). We mirror it
     here only so we never OFFER a slot we know will be refused.
     Keep the two in step: raise this without raising the backend and the UI
     hides slots the server would have taken; lower it and the UI offers slots
     the server refuses, which reads to the customer as a broken button. */
  var REQUEST_LEAD_MS = 10 * 60 * 1000;
  function tooSoonToRequest(dateIso, startHHMM) {
    return new Date(dateIso + 'T' + startHHMM + ':00+05:30').getTime() - Date.now() < REQUEST_LEAD_MS;
  }

  /* All calendar maths runs on UTC-midnight Dates built from yyyy-mm-dd strings,
     so the viewer's timezone can never shift the grid by a day. */
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function ymd(y, m, d) { return y + '-' + pad(m + 1) + '-' + pad(d); }
  function utc(iso) { return new Date(iso + 'T00:00:00Z'); }
  function prettyDate(iso) {
    return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).format(utc(iso));
  }
  function shortDate(iso) {
    return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short' }).format(utc(iso)).toUpperCase();
  }

  /* opts:
       mount      — element to render into
       nets       — [{value,label}]
       initialNet — default 'Net 1'
       maxDays    — booking horizon (default 180)
       autoOnly   — true for the CLINIC: slots outside 11 AM–7 PM are shown but
                    disabled. The clinic is paid at checkout, so it cannot be
                    "requested" — a rejection would owe a refund. Net sessions
                    leave this false: off-hours is bookable as an owner-approved
                    request, because a rejection just returns the credit.
       loadSlots  — function({net,date}) -> Promise<{isWorkDay, slots:[{start,end,available,mode}]}>
       onChange   — called whenever net/date changes (start resets to null)
       onSlot     — called with (start|null, slot|null) when a slot is picked or cleared
  */
  function calendar(opts) {
    var mount = opts.mount;
    var maxDays = opts.maxDays || MAX_DAYS;
    var minDate = S.istDate(0);
    /* opts.maxDate (yyyy-mm-dd) hard-caps the last bookable day — used to stop a
       booking landing past a package's expiry (rule A). Never beyond the horizon,
       never before today. Month-nav bounds and day-disabling all derive from
       maxDate, so nothing else needs to change. */
    var horizon = S.istDate(maxDays);
    var maxDate = (opts.maxDate && opts.maxDate < horizon) ? opts.maxDate : horizon;
    if (maxDate < minDate) maxDate = minDate;

    var state = { net: opts.initialNet || 'Net 1', date: minDate, start: null, slot: null };
    var view = { y: +minDate.slice(0, 4), m: +minDate.slice(5, 7) - 1 };
    var reqToken = 0;

    /* ── Net chips ── */
    var netWrap = S.el('div', 'sc-nsm__field');
    netWrap.appendChild(S.el('span', 'sc-nsm__label', 'Net'));
    netWrap.appendChild(S.choices(opts.nets, function (v) {
      state.net = v; setStart(null); refresh();
      if (opts.onChange) opts.onChange(state);
    }, state.net));
    mount.appendChild(netWrap);

    /* ── Calendar pane ── */
    var grid = S.el('div', 'sc-nsm__grid');
    var calPane = S.el('div', 'sc-nsm__pane');

    var calHead = S.el('div', 'sc-nsm__calhead');
    var prevBtn = navButton('‹', 'Previous month');
    var monLabel = S.el('div', 'sc-nsm__calmon');
    var nextBtn = navButton('›', 'Next month');
    calHead.appendChild(prevBtn);
    calHead.appendChild(monLabel);
    calHead.appendChild(nextBtn);
    calPane.appendChild(calHead);

    var dows = S.el('div', 'sc-nsm__dows');
    ['M', 'T', 'W', 'T', 'F', 'S', 'S'].forEach(function (d, i) {
      var c = S.el('div', 'sc-nsm__dow', d);
      c.setAttribute('aria-hidden', 'true');
      c.title = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][i];
      dows.appendChild(c);
    });
    calPane.appendChild(dows);

    var daysBox = S.el('div', 'sc-nsm__days');
    daysBox.setAttribute('role', 'grid');
    calPane.appendChild(daysBox);
    calPane.appendChild(S.el('p', 'sc-nsm__callegend', 'Nets run Tue–Sun · Mondays closed'));

    /* ── Slots pane ── */
    var slotPane = S.el('div', 'sc-nsm__pane');
    var paneHead = S.el('div', 'sc-nsm__panehead');
    var paneTitle = S.el('span', 'sc-nsm__panetitle', '');
    var paneCount = S.el('span', 'sc-nsm__panecount', '');
    paneHead.appendChild(paneTitle);
    paneHead.appendChild(paneCount);
    slotPane.appendChild(paneHead);

    var scroll = S.el('div', 'sc-nsm__slotscroll');
    var slotsBox = S.el('div', 'sc-nsm__slots');
    var paneMsg = S.el('p', 'sc-nsm__panemsg', '');
    scroll.appendChild(slotsBox);
    scroll.appendChild(paneMsg);
    slotPane.appendChild(scroll);

    grid.appendChild(calPane);
    grid.appendChild(slotPane);
    mount.appendChild(grid);

    function navButton(glyph, label) {
      var b = S.el('button', 'sc-nsm__navbtn', glyph);
      b.type = 'button';
      b.setAttribute('aria-label', label);
      return b;
    }
    function setStart(v, slot) {
      state.start = v;
      state.slot = v ? (slot || null) : null;
      if (opts.onSlot) opts.onSlot(v, state.slot);
    }

    var monthIndex = function (y, mo) { return y * 12 + mo; };
    var minIdx = monthIndex(+minDate.slice(0, 4), +minDate.slice(5, 7) - 1);
    var maxIdx = monthIndex(+maxDate.slice(0, 4), +maxDate.slice(5, 7) - 1);

    function renderCalendar() {
      monLabel.textContent = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' })
        .format(new Date(Date.UTC(view.y, view.m, 1)));
      prevBtn.disabled = monthIndex(view.y, view.m) <= minIdx;
      nextBtn.disabled = monthIndex(view.y, view.m) >= maxIdx;

      daysBox.innerHTML = '';
      // Monday-first: getUTCDay() is 0=Sun, so shift by 6 mod 7.
      var lead = (new Date(Date.UTC(view.y, view.m, 1)).getUTCDay() + 6) % 7;
      for (var i = 0; i < lead; i++) {
        var blank = S.el('div', 'sc-nsm__day sc-nsm__day--blank');
        blank.setAttribute('aria-hidden', 'true');
        daysBox.appendChild(blank);
      }
      var daysInMonth = new Date(Date.UTC(view.y, view.m + 1, 0)).getUTCDate();
      for (var d = 1; d <= daysInMonth; d++) {
        (function (day) {
          var iso = ymd(view.y, view.m, day);
          // Mondays are closed (must match CFG.WORK_DAYS on the backend).
          var isClosed = new Date(Date.UTC(view.y, view.m, day)).getUTCDay() === 1;
          var outOfRange = iso < minDate || iso > maxDate;

          var cell = S.el('button', 'sc-nsm__day', String(day));
          cell.type = 'button';
          cell.disabled = isClosed || outOfRange;
          if (iso === minDate) cell.className += ' sc-nsm__day--today';
          cell.setAttribute('aria-selected', String(iso === state.date));
          cell.setAttribute('aria-label', prettyDate(iso) + (isClosed ? ', closed (Monday)' : ''));
          if (!cell.disabled) {
            cell.addEventListener('click', function () {
              state.date = iso;
              setStart(null);
              /* Update selection in place. Re-rendering the grid here would
                 destroy the button just clicked and drop keyboard focus. */
              daysBox.querySelectorAll('.sc-nsm__day').forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
              cell.setAttribute('aria-selected', 'true');
              refresh();
              if (opts.onChange) opts.onChange(state);
            });
          }
          daysBox.appendChild(cell);
        }(d));
      }
    }

    function setPaneMessage(text) {
      slotsBox.innerHTML = '';
      paneMsg.textContent = text;
      paneCount.textContent = '';
    }

    /* Re-ask the backend for this (net, date). A stale response from a slower
       earlier request must never overwrite a newer one — hence reqToken. */
    function refresh() {
      setStart(null);
      paneTitle.textContent = state.date ? shortDate(state.date) : '';
      var ready = opts.canLoad ? opts.canLoad(state) : true;
      if (ready !== true) { setPaneMessage(typeof ready === 'string' ? ready : 'Choose an option first.'); return; }

      setPaneMessage('Loading times…');
      var token = ++reqToken;

      opts.loadSlots(state)
        .then(function (res) {
          if (token !== reqToken) return;
          if (!res.isWorkDay) { setPaneMessage('Closed on this day; the nets run Tuesday to Sunday.'); return; }

          /* A slot outside 11 AM–7 PM is drawn dashed. For a net session it is
             still clickable — it becomes an owner-approved request — UNLESS it
             starts too soon for the owner to answer. For the clinic (autoOnly)
             every off-hours slot is shown but disabled. */
          var tooSoon = function (s) { return s.mode !== 'auto' && !opts.autoOnly && tooSoonToRequest(state.date, s.start); };
          var pickable = function (s) {
            if (!s.available) return false;
            if (s.mode === 'auto') return true;
            return !opts.autoOnly && !tooSoonToRequest(state.date, s.start);
          };
          var bookable = res.slots.filter(pickable).length;
          var offHours = res.slots.filter(function (s) { return pickable(s) && s.mode !== 'auto'; }).length;
          var blocked  = res.slots.filter(function (s) { return s.available && s.mode !== 'auto' && !pickable(s); }).length;
          slotsBox.innerHTML = '';
          paneMsg.textContent = bookable ? '' :
            'No times left on ' + state.net + ' this day. Try the other net, or another date.';
          paneCount.textContent = bookable + ' bookable' +
            (offHours ? ' · ' + offHours + ' need confirming' : '') +
            (blocked ? ' · ' + blocked + (opts.autoOnly ? ' outside 11–7 (call us)' : ' too soon') : '');

          res.slots.forEach(function (slot) {
            var isAuto = slot.mode === 'auto';
            /* Show both ends of the slot, in 12-hour AM/PM. A 30-min and a 60-min
               session are indistinguishable from the start time alone. */
            var b = S.el('button', 'sc-nsm__slot' + (isAuto ? '' : ' sc-nsm__slot--offhours'), S.fmtRange(slot.start, slot.end));
            b.type = 'button';
            b.disabled = !pickable(slot);
            b.setAttribute('aria-pressed', 'false');
            b.title = S.fmtRange(slot.start, slot.end) + ' on ' + state.net + (isAuto ? '' :
              (opts.autoOnly
                ? ' · outside standard hours (11 AM–7 PM): please call us to book'
                : (tooSoon(slot)
                    ? ' · outside standard hours (11 AM–7 PM): too soon for us to confirm it; please call us'
                    : ' · outside standard hours (11 AM–7 PM): the owner confirms this slot')));
            if (pickable(slot)) {
              b.addEventListener('click', function () {
                slotsBox.querySelectorAll('.sc-nsm__slot').forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
                b.setAttribute('aria-pressed', 'true');
                setStart(slot.start, slot);
              });
            }
            slotsBox.appendChild(b);
          });
        })
        .catch(function (err) {
          if (token !== reqToken) return;
          setPaneMessage('');
          if (opts.onError) opts.onError(err);
        });
    }

    prevBtn.addEventListener('click', function () {
      if (--view.m < 0) { view.m = 11; view.y--; }
      renderCalendar();
    });
    nextBtn.addEventListener('click', function () {
      if (++view.m > 11) { view.m = 0; view.y++; }
      renderCalendar();
    });

    renderCalendar();
    refresh();

    return {
      state: state,
      refresh: refresh,
      renderCalendar: renderCalendar,
      prettyDate: prettyDate
    };
  }

  S.calendar = calendar;
  S.prettyDate = function (iso) { return prettyDate(iso); };
}());
